import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ADDITIONAL_SERVICE_SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { isTransportProduct } from '@/shared/domain/product/isTransportProduct.ts';
import { deriveQuantity } from '../utils/deriveQuantity.ts';

export function useAdditionalServiceQuantities(methods: UseFormReturn<AutomaticProductsFormValues>) {
    const { getValues, setValue } = methods;

    const getSourceQuantity = useCallback(() => {
        const [
            scope,
            productType,
            vehicleClass,
            transportNumber,
            salesMaterialQuantity,
            salesMaterialUnit,
            salesTransportQuantity,
            salesTransportUnit,
        ] = getValues([
            'scope',
            'productType',
            'vehicleClass',
            'transportNumber',
            'salesMaterialQuantity',
            'salesMaterialUnit',
            'salesTransportQuantity',
            'salesTransportUnit',
        ]);
        const payloadInTons = vehicleClass?.payloadInTons;

        if (scope === 'ORDER') {
            return { qty: transportNumber, unit: 'TRANSPORT' as const, payloadInTons };
        }
        if (isTransportProduct(productType)) {
            return { qty: salesTransportQuantity, unit: salesTransportUnit, payloadInTons };
        }
        return { qty: salesMaterialQuantity, unit: salesMaterialUnit, payloadInTons };
    }, [getValues]);

    const onSalesUnitChange = useCallback(
        (index: number, salesUnit?: ADDITIONAL_SERVICE_SUPPORTED_UNITS) => {
            if (!salesUnit) {
                return;
            }
            const { qty, unit, payloadInTons } = getSourceQuantity();
            setValue(`additionalServices.${index}.salesQuantity`, deriveQuantity(qty, unit, salesUnit, payloadInTons));

            const purchaseUnit = getValues(`additionalServices.${index}.purchaseUnit`);

            if (purchaseUnit) {
                setValue(
                    `additionalServices.${index}.purchaseQuantity`,
                    deriveQuantity(qty, unit, purchaseUnit, payloadInTons)
                );
            }
        },
        [getSourceQuantity, getValues, setValue]
    );

    const onPurchaseUnitChange = useCallback(
        (index: number, purchaseUnit?: ADDITIONAL_SERVICE_SUPPORTED_UNITS) => {
            if (!purchaseUnit) {
                return;
            }
            const { qty, unit, payloadInTons } = getSourceQuantity();
            setValue(
                `additionalServices.${index}.purchaseQuantity`,
                deriveQuantity(qty, unit, purchaseUnit, payloadInTons)
            );
        },
        [getSourceQuantity, setValue]
    );

    const getDefaultQuantitiesForNewRow = useCallback(
        ({
            salesUnit,
            purchaseUnit,
        }: {
            salesUnit?: ADDITIONAL_SERVICE_SUPPORTED_UNITS;
            purchaseUnit?: ADDITIONAL_SERVICE_SUPPORTED_UNITS;
        } = {}) => {
            const { qty, unit, payloadInTons } = getSourceQuantity();
            return {
                salesQuantity: salesUnit ? deriveQuantity(qty, unit, salesUnit, payloadInTons) : undefined,
                purchaseQuantity: purchaseUnit ? deriveQuantity(qty, unit, purchaseUnit, payloadInTons) : undefined,
            };
        },
        [getSourceQuantity]
    );

    return { onSalesUnitChange, onPurchaseUnitChange, getDefaultQuantitiesForNewRow };
}
